import { useAgentStore } from '@/stores/agentStore';
import {
  createLocalId,
  buildAssistantIntro, formatFinalAssistantText,
  extractAllContentFromText,
  stripThinkingAndToolCallTags,
} from '../helpers';
import { createSearchMessage, normalizeSearchData } from '../search-result-utils';
import { Message } from '../types';
import { searchSessionMap } from '../streamState';
// 🔵 使用统一的 ProcessLogs 管理器，消除 4 处分散的解析逻辑
import {
  addProcessLogFromCollabStatus,
  addProcessLogFromSubAgent,
  addActiveProcessLog,
  markThinkingLogComplete,
  getActiveProcessLogs,
} from '../processLogManager';

export interface StreamCallbackDeps {
  addActiveProcessLog: (kind: any, title: string, detail?: string, status?: any) => void;
  finalizeAssistantMessage: (msg: any) => Promise<void>;
  updateAssistantMessage: (id: string, updates: Partial<Message>) => void;
  // 🔴 v8-#7: 移除未使用的 accumulatedTextRef（死代码）
  getCurrentIntent: () => { name: string; confidence: number; source: string } | null;
  setCurrentIntent: (intent: { name: string; confidence: number; source: string }) => void;
  userText: string;
  currentAttachments: any[];
  currentRefs: any[];
  attachmentPayloads: any[];
  referencePayloads: any[];
}

export function createOnAgentIdCallback(deps: Pick<StreamCallbackDeps, 'addActiveProcessLog'>) {
  return (agentId: string) => {
    useAgentStore.getState().setCurrentAgentId(agentId);
    useAgentStore.getState().updateActiveProcessLogs((prev) => prev.map(l =>
      l.title === '🧭 正在调度' && l.status === 'active'
        ? { ...l, status: 'completed', title: '🧭 调度完成' }
        : l
    ));
    const agents = useAgentStore.getState().agents;
    const profile = agents.find(a => a.id === agentId);
    if (profile && profile.id !== 'bong-assistant') {
      deps.addActiveProcessLog(
        'context',
        `🧭 调度分配 → ${profile.avatar || ''} ${profile.name}`,
        `任务已指派给 ${profile.name} 负责，由其主导整个任务流程`,
        'completed'
      );
    } else if (!profile) {
      const fallbackName = agentId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      deps.addActiveProcessLog(
        'context',
        `🧭 调度分配 → ${fallbackName}`,
        `任务已指派给 ${fallbackName} 负责`,
        'completed'
      );
    }
  };
}

export function createOnIntentCallback(deps: Pick<StreamCallbackDeps, 'userText' | 'currentAttachments' | 'currentRefs' | 'setCurrentIntent'>) {
  return (intent: { name: string; confidence: number; source: string; params?: Record<string, any> }) => {
    deps.setCurrentIntent(intent);
    useAgentStore.getState().setCurrentIntent(intent.name);
    const refinedIntro = buildAssistantIntro({
      userText: deps.userText,
      intentName: intent.name,
      hasAttachments: deps.currentAttachments.length > 0,
      hasReferences: deps.currentRefs.length > 0,
    });
    useAgentStore.getState().setActiveAssistantIntro(refinedIntro);
  };
}

export function createOnThinkingCallback(deps: Pick<StreamCallbackDeps, 'addActiveProcessLog'>) {
  let accumulatedThinking = '';
  return (thinkingContent: string) => {
    // 🔴 v6-#9: 检查是否为新的 thinking 会话（当前无活跃的 thinking 日志）
    // 如果已有活跃 thinking 日志，accumulatedThinking 不为空，只更新内容
    // 如果没有活跃 thinking 日志（新会话或重置后），重新初始化
    const store = useAgentStore.getState();
    const hasActiveThinking = store.activeProcessLogs.some(l => l.kind === 'thinking' && l.status === 'active');

    if (!accumulatedThinking || !hasActiveThinking) {
      // 新的 thinking 会话
      accumulatedThinking = ''; // 重置，避免旧数据残留
      store.setIsThinking(true);
      store.setThinkingSeconds(0);
      store.startThinkingTimer();
      deps.addActiveProcessLog('thinking', '🧠 思考中', thinkingContent, 'active');
    } else {
      store.updateActiveProcessLogs((prev) => {
        const thinkLog = prev.find(l => l.kind === 'thinking' && l.status === 'active');
        if (thinkLog) {
          return prev.map(l => l.id === thinkLog.id ? { ...l, detail: accumulatedThinking + thinkingContent } : l);
        }
        return prev;
      });
    }
    accumulatedThinking += thinkingContent;
  };
}

// 🔵 使用统一的 ProcessLogs 管理器：消除重复的 COLLAB_STATUS 解析逻辑
export function createOnCollabStatusCallback() {
  return (content: string) => {
    addProcessLogFromCollabStatus(content);
  };
}

// 🔵 使用统一的 ProcessLogs 管理器：消除重复的子智能体思考日志逻辑
export function createOnSubThinkingCallback() {
  return (agentId: string, content: string) => {
    addProcessLogFromSubAgent(agentId, 'thinking', content);
  };
}

// 🔵 使用统一的 ProcessLogs 管理器：消除重复的子智能体输出日志逻辑
export function createOnSubOutputCallback() {
  return (agentId: string, content: string) => {
    addProcessLogFromSubAgent(agentId, 'output', content);
  };
}

// 使用 Map 管理多个搜索会话的状态（支持多会话并发）
const MAX_SESSIONS = 100;
// 🔴 优化：统一前后端冷却期为 30 秒（与后端 SearchOrchestrator.cooldown_seconds 一致）
const SESSION_TTL_MS = 30 * 1000; // 30 秒过期

// 清理旧会话机制，防止内存泄漏
function cleanupOldSessions() {
  const now = Date.now();
  // 🔴 v9-#5: 清理过期的会话（超过 TTL）
  for (const [key, state] of searchSessionMap.entries()) {
    if (now - state.lastAccessTime > SESSION_TTL_MS) {
      searchSessionMap.delete(key);
    }
  }
  // 保留数量上限
  if (searchSessionMap.size > MAX_SESSIONS) {
    const entries = Array.from(searchSessionMap.entries());
    searchSessionMap.clear();
    // 保留最近 50 个会话
    entries.slice(-50).forEach(([k, v]) => searchSessionMap.set(k, v));
    console.log(`[搜索结果回调] 已清理旧会话，保留最近 50 个会话`);
  }
}

// 搜索阶段的优先级排序
const PHASE_PRIORITY: Record<string, number> = {
  'search_results': 1,
  'crawled_details': 2,
  'summary_streaming': 3,
  'final': 4,
};

function isNewerPhase(existingPhase: string | null, newPhase: string | null): boolean {
  if (!existingPhase && newPhase) return true;
  if (!newPhase) return false;
  const existingPriority = existingPhase ? (PHASE_PRIORITY[existingPhase] || 0) : 0;
  const newPriority = PHASE_PRIORITY[newPhase] || 0;
  return newPriority > existingPriority;
}

// 🔵 P2: 使用 ActiveReplyBuilder - 搜索结果回调不再直接操作 messages，而是更新构建器
// 由 handleComplete 在流结束时统一生成消息，彻底消除"搜索消息 + 文本消息"的双路竞态
export function createOnSearchResultCallback(
  deps: {
    getBackendAssistantMsgId: () => string | null,
  }
) {
  return (data: any) => {
    console.log('[搜索结果回调] 收到数据:', data);

    try {
      if (!data) {
        console.warn('[搜索结果回调] 空数据');
        return;
      }

      // 🔵 P1-5: 标志位已移除，通过 builder.type === 'search' 作为场景判断
      // 设置 builder.searchData，由 chunk handler 通过 builder.type 抑制后续文本流
      const normalizedData = normalizeSearchData(data);
      normalizedData._raw = data;
      normalizedData._fallback = data._fallback || false;
      normalizedData._searchSessionId = data._searchSessionId;
      normalizedData._version = data._version;
      normalizedData._phase = data._phase;

      const sessionId = normalizedData._searchSessionId;
      const phase = normalizedData._phase || data.phase;

      // 会话状态管理（版本号 / phase 优先级 / 清理旧会话，逻辑保持不变）
      if (sessionId !== undefined) {
        let sessionState = searchSessionMap.get(sessionId);
        if (normalizedData._version !== undefined) {
          if (sessionState && normalizedData._version < sessionState.version) {
            console.log(`[搜索结果回调] 忽略过期版本的数据，会话=${sessionId}，版本=${normalizedData._version} < ${sessionState.version}`);
            return;
          }
          if (!sessionState) {
            sessionState = { version: 0, phase: null, latestData: null, processedPhases: new Set(), lastAccessTime: Date.now() };
            searchSessionMap.set(sessionId, sessionState);
          }
          sessionState.version = normalizedData._version;
          sessionState.lastAccessTime = Date.now();
        }
        if (phase && sessionState) {
          if (sessionState.processedPhases.has(phase)) {
            console.log(`[搜索结果回调] 跳过已处理的 phase: ${phase}`);
            return;
          }
          if (!isNewerPhase(sessionState.phase, phase)) {
            console.log(`[搜索结果回调] 跳过低优先级 phase: ${phase} (已有: ${sessionState.phase})`);
            return;
          }
          sessionState.phase = phase;
        }
        // 🔴 P5修复: 始终将已处理的 phase 添加到 processedPhases
        // 之前仅当 _version === undefined && phase 时才标记，导致带版本号的消息同一 phase 被处理多次
        if (sessionState && phase) {
          sessionState.processedPhases.add(phase);
        }
      }
      cleanupOldSessions();

      const backendMsgId = deps.getBackendAssistantMsgId();
      const finalId = backendMsgId || useAgentStore.getState().activeReplyBuilder?.id || createLocalId();

      // 🔵 P1-2: search 场景也同步设置 pendingBackendMsgIds
      useAgentStore.getState().setPendingBackendMsgIds({ assistant_msg_id: finalId });

      // 🔴 v8-#3: 检查 builder 是否存在，若已被清除（如用户停止生成）则重新初始化
      // 🔴 v9-#4: 若已 abort（无 abortController），不重新初始化 builder，避免 abort 后回调竞争
      // 🔵 若流式会话已结束（streamEnded），不重新初始化 builder，避免僵尸搜索卡片
      const store = useAgentStore.getState();
      if (!store.activeReplyBuilder && (!store.abortController || store.streamEnded)) {
        console.log('[搜索结果回调] 已 abort 或流已结束，跳过 builder 重新初始化');
        return;
      }
      if (!store.activeReplyBuilder) {
        store.initActiveReplyBuilder();
      }

      // 🔵 P2: 统一更新 ActiveReplyBuilder - 把搜索数据写入 builder，而非直接操作 messages
      // 由 handleComplete 在流结束时把 builder 转为正式消息
      // 🔵 P1-1: 同步设置 introText，使搜索消息在历史消息中有完整引言
      // 🔴 P2-修复: 条件覆盖 builder.text，避免覆盖 chunk handler 累积的流式摘要文本
      // 🔴 P1-优化: 只有在 summary 或 query 有真实内容时才写 introText/text，避免 `搜索「」的结果` 这种空占位；
      //              若两者都为空，把 summary 的渲染让给后续的 summary_streaming chunk
      // P3修复：清除 summary 中的 [TOOL_CALL] 标签，避免标签被写入 builder.text
      // P6(v6): preserveWhitespace=true 保留换行符，避免 markdown 格式丢失；
      //          保留一份 cleanedSummaryForText（.trim()）用于 introText/plain text 场景
      const cleanedSummary = normalizedData.summary
          ? stripThinkingAndToolCallTags(normalizedData.summary, true)
          : '';
      const cleanedSummaryForText = cleanedSummary.trim();
      const hasRealSummary = !!cleanedSummaryForText && cleanedSummaryForText.length > 0;
      const hasRealQuery = !!normalizedData.query && normalizedData.query.trim().length > 0;
      // P0(v9): introText 始终使用简短描述，不使用完整摘要
      // 完整摘要由流式 chunk handler 逐步构建到 builder.text
      // 如果将完整摘要写入 builder.text，后续流式 chunk 追加会导致内容重复
      const introText = hasRealQuery
          ? `搜索「${normalizedData.query}」的结果`
          : hasRealSummary
            ? cleanedSummaryForText.length > 30
              ? cleanedSummaryForText.slice(0, 30) + '...'
              : cleanedSummaryForText
            : null;
      const currentBuilder = useAgentStore.getState().activeReplyBuilder;
      const currentText = currentBuilder?.text;
      // 🔴 P0(v10): 首次从 text 阶段切换到 search 阶段时，清除旧文本
      // 避免主 LLM 输出的文本残留在 builder.text 中，导致搜索卡片先显示纯文本再切换为格式化内容
      const isPhaseTransition = currentBuilder && currentBuilder.type !== 'search';
      if (isPhaseTransition) {
        useAgentStore.getState().updateActiveReplyBuilder({
          text: '',
          pendingMarkdown: undefined,
        });
      }
      // P0(v9): 仅当 builder.text 为空或是旧 introText 时才设置
      // 且只设置简短 introText，不设置完整摘要
      // 🔴 P0(v10): phase 切换后 currentText 已被清空，shouldUpdateText 始终为 true
      const effectiveText = isPhaseTransition ? '' : currentText;
      const shouldUpdateText = introText !== null && (!effectiveText || effectiveText === currentBuilder?.introText);
      // P1修复：合并 searchData，避免后续推送的不完整数据覆盖已有完整数据
      const existingSearchData = useAgentStore.getState().activeReplyBuilder?.searchData;
      const mergedSearchData = existingSearchData
          ? {
              ...existingSearchData,
              ...normalizedData,
              // 保留已有数据：新数据为空时不覆盖
              results: normalizedData.results?.length > 0 ? normalizedData.results : existingSearchData.results,
              query: normalizedData.query || existingSearchData.query,
              // P6(v6): 使用清理后的 summary（去除标签但保留格式），而非原始 normalizedData.summary
              summary: cleanedSummary || existingSearchData.summary,
              // 保留 _raw 和 _fallback 标志
              _raw: normalizedData._raw || existingSearchData._raw,
              _fallback: normalizedData._fallback || existingSearchData._fallback,
            }
          : (cleanedSummary
              ? { ...normalizedData, summary: cleanedSummary }
              : normalizedData);

      useAgentStore.getState().updateActiveReplyBuilder({
        id: finalId,
        type: 'search',
        // P0(v9): introText 始终为简短描述
        ...(introText !== null ? { introText } : {}),
        searchData: mergedSearchData,
        // P0(v9): text 只设置简短 introText，完整摘要由流式 chunk 逐步构建
        ...(shouldUpdateText ? { text: introText } : {}),
      });
      console.log('[搜索结果回调] 已更新 ActiveReplyBuilder（type=search）');
    } catch (error) {
      console.error('[搜索结果回调] 处理失败:', error);
    }
  };
}

// 🔵 P2: 使用 ActiveReplyBuilder - 生成确认回调同样更新构建器，而非直接追加消息
// 流结束时由 handleComplete 统一生成最终消息，避免"文本消息 + 确认面板"双路竞态
export function createOnGenerationConfirmCallback(
  deps: {
    userText: string;
    currentAttachments: any[];
    currentRefs: any[];
    attachmentPayloads: any[];
    referencePayloads: any[];
    getBackendAssistantMsgId?: () => string | null;
  }
) {
  return async (data: { content: string; message?: string; generationOptions: any; intent: any; messageType: string; assistantMsgId?: string }) => {
    // 🔵 P1-5: 标志位已移除，通过 builder.type === 'generation_confirm' 作为场景判断
    // 🔵 P1-2: search/generation_confirm 场景也同步设置 pendingBackendMsgIds
    const finalId = data.assistantMsgId || useAgentStore.getState().activeReplyBuilder?.id || createLocalId();
    useAgentStore.getState().setPendingBackendMsgIds({ assistant_msg_id: finalId });

    // 🔴 v8-#3: 检查 builder 是否存在，若已被清除则重新初始化
    // 🔴 v9-#4: 若已 abort（无 abortController），不重新初始化 builder，避免 abort 后回调竞争
    // 🔵 若流式会话已结束（streamEnded），不重新初始化 builder
    const store = useAgentStore.getState();
    if (!store.activeReplyBuilder && (!store.abortController || store.streamEnded)) {
      console.log('[生成确认回调] 已 abort 或流已结束，跳过 builder 重新初始化');
      return;
    }
    if (!store.activeReplyBuilder) {
      store.initActiveReplyBuilder();
    }

    // 🟠 v4-#4: 优先使用 message 字段（后端已将嵌套的 content 重命名为 message），兼容旧版 content
    const textContent = data.message || data.content || '请确认生成参数';

    // 🔵 P2: 直接写入 ActiveReplyBuilder，生成包含参数面板所需数据
    // 🔵 P1-1: 同步设置 introText，使搜索/确认消息在历史消息中有完整引言
    useAgentStore.getState().updateActiveReplyBuilder({
      id: finalId,
      type: 'generation_confirm',
      introText: textContent,
      text: textContent,
      generationOptions: data.generationOptions,
      // 把用户输入 / 附件 / 引用 写入 metadata，供确认面板（Confirmation Panel）取用
      metadata: {
        pendingUserText: deps.userText,
        pendingAttachments: deps.attachmentPayloads.length > 0 ? deps.attachmentPayloads : undefined,
        pendingReferences: deps.referencePayloads.length > 0 ? deps.referencePayloads : undefined,
        pendingRefNodeIds: deps.currentRefs.length > 0 ? deps.currentRefs.map((r: any) => r.id) : undefined,
      },
    });
    console.log('[生成确认回调] 已更新 ActiveReplyBuilder（type=generation_confirm）');
  };
}
